/**
 * social.ts — social posts that are written here and approved by a person other than their author.
 *
 * Channels are typed in by a person (a name and a network). A post that is approved is "ready to publish"; the CMS
 * does not publish it itself yet.
 */
import { z } from "zod";
import { zonedToInstant } from "./timezone.ts";
import { adProblemSchema } from "./adsProducts.ts";

export const SOCIAL_STATUSES = ["draft", "pending_review", "approved", "cancelled"] as const;
export const socialStatusSchema = z.enum(SOCIAL_STATUSES);
export type SocialStatus = z.infer<typeof socialStatusSchema>;

export const SOCIAL_STATUS_LABEL: Record<SocialStatus, string> = {
  draft: "Draft",
  pending_review: "Waiting for approval",
  approved: "Approved, ready to publish",
  cancelled: "Cancelled",
};

const TRANSITIONS: Record<SocialStatus, readonly SocialStatus[]> = {
  draft: ["pending_review", "cancelled"],
  pending_review: ["approved", "draft", "cancelled"],
  approved: ["draft", "cancelled"],
  cancelled: [],
};
export function isValidSocialTransition(from: SocialStatus, to: SocialStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Longest text each network accepts, as published by the networks. Anything not listed is not checked. */
const LIMITS: Array<{ match: RegExp; label: string; max: number }> = [
  { match: /^linkedin/, label: "LinkedIn", max: 3000 },
  { match: /^(twitter|x)$/, label: "X", max: 280 },
  { match: /^instagram/, label: "Instagram", max: 2200 },
  { match: /^threads/, label: "Threads", max: 500 },
  { match: /^bluesky/, label: "Bluesky", max: 300 },
  { match: /^mastodon/, label: "Mastodon", max: 500 },
  { match: /^facebook/, label: "Facebook", max: 63206 },
  { match: /^tiktok/, label: "TikTok", max: 150 },
];
export function socialTextLimit(service: string): { label: string; max: number } | null {
  const s = service.trim().toLowerCase();
  const hit = LIMITS.find((l) => l.match.test(s));
  return hit ? { label: hit.label, max: hit.max } : null;
}
/** Characters as the networks count them (code points, not UTF-16 units, so an emoji is one). */
export const socialLength = (text: string): number => [...text].length;

export function socialTextProblem(service: string, text: string): string | null {
  const t = text.trim();
  if (!t) return "Write the post text.";
  const limit = socialTextLimit(service);
  if (limit && socialLength(t) > limit.max) return `${limit.label} allows ${limit.max.toLocaleString()} characters; this is ${socialLength(t).toLocaleString()}.`;
  // Sociamonials: "Tweets cannot contain links" (only some enterprise workspaces may post a few a month).
  if (/^(twitter|x)$/.test(service.toLowerCase()) && /(https?:\/\/|\bwww\.)\S+/i.test(t)) return "X posts sent through Sociamonials cannot contain links. Remove the link, or post to the other networks.";
  return null;
}

// --- pictures and video on a post ---

/** The most pictures each network takes in one post, from Sociamonials' documentation. Others are not limited here. */
const IMAGE_MAX: Array<{ match: RegExp; label: string; max: number }> = [
  { match: /^(twitter|x)$/, label: "X", max: 4 },
  { match: /^bluesky/, label: "Bluesky", max: 4 },
  { match: /^instagram/, label: "Instagram", max: 10 },
  { match: /^threads/, label: "Threads", max: 10 },
];
export const SOCIAL_MAX_IMAGES = 10;
export const YOUTUBE_TITLE_MAX = 100;

const mediaUrl = z
  .string()
  .trim()
  .max(1000)
  .refine((u) => /^https:\/\/[^\s]+$/i.test(u), { message: "A media link must be a direct https link to the file." });
export const socialImageUrlsSchema = z.array(mediaUrl).max(SOCIAL_MAX_IMAGES).default([]);

export interface SocialMediaFields {
  imageUrls: readonly string[];
  videoUrl: string | null;
  youtubeTitle: string | null;
}

/**
 * What a network needs before Sociamonials will take the post. Plain words; null = fine.
 * stage "draft" checks only what can never be right (pictures and a video together, too many pictures), so a post can be
 * saved while it is still being put together; "ready" (asking for approval, sending) also requires what the network needs.
 */
export function socialMediaProblem(service: string, m: SocialMediaFields, stage: "draft" | "ready" = "ready"): string | null {
  const s = service.trim().toLowerCase();
  const hasImages = m.imageUrls.length > 0;
  const hasVideo = !!m.videoUrl;
  if (hasImages && hasVideo) return "Use pictures or a video, not both.";
  const cap = IMAGE_MAX.find((l) => l.match.test(s));
  if (cap && m.imageUrls.length > cap.max) return `${cap.label} takes at most ${cap.max} pictures in one post.`;
  if (stage === "draft") return null;
  if (/^instagram/.test(s) && !hasImages && !hasVideo) return "Instagram needs a picture or a video.";
  if (/^tiktok/.test(s) && !hasImages && !hasVideo) return "TikTok needs a picture or a video.";
  if (/^youtube/.test(s)) {
    if (!hasVideo) return "YouTube needs a video link.";
    if (!m.youtubeTitle?.trim()) return "Give the YouTube video a title.";
  }
  return null;
}

export const socialChannelSchema = z.object({
  channelId: z.string(),
  siteId: z.string(),
  name: z.string(),
  service: z.string(),
  enabled: z.boolean(),
  createdAt: z.string(),
  /** A company page, or a teammate's own profile. */
  kind: z.enum(["company", "teammate"]).default("company"),
  /** Whose brand kit checks this channel's posts, or null for none. */
  brandProduct: z.enum(["sonic_hire", "raya"]).nullable().default(null),
  /** Preferred send times (site clock). Empty means the general B2B times are used. */
  sendTimes: z.array(z.object({ weekday: z.number().int().min(1).max(7), minutes: z.number().int().min(0).max(1439) })).max(35).default([]),
  /** Linked to a Sociamonials profile, and whether Sociamonials says it needs signing in again. */
  smLinked: z.boolean().default(false),
  smNeedsReconnect: z.boolean().default(false),
  smAlert: z.string().nullable().default(null),
  /** From our own posts: the last send, and how many went out this month. */
  lastSentAt: z.string().nullable().default(null),
  sentThisMonth: z.number().int().default(0),
  /** The last "Test send" (a dry run; nothing is created at Sociamonials). */
  lastTestAt: z.string().nullable().default(null),
  lastTestOk: z.boolean().nullable().default(null),
  lastTestMessage: z.string().nullable().default(null),
});
export type SocialChannel = z.infer<typeof socialChannelSchema>;

export type UpdateSocialChannelBody = z.infer<typeof updateSocialChannelBodySchema>;
export const updateSocialChannelBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  enabled: z.boolean().optional(),
  kind: z.enum(["company", "teammate"]).optional(),
  brandProduct: z.enum(["sonic_hire", "raya"]).nullable().optional(),
  sendTimes: z.array(z.object({ weekday: z.number().int().min(1).max(7), minutes: z.number().int().min(0).max(1439) })).max(35).optional(),
});

/** The networks a channel can be on, as the person sees them. The value is what the length rules and the AI helper read. */
export const SOCIAL_NETWORK_CHOICES = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "x", label: "X" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "threads", label: "Threads" },
  { value: "bluesky", label: "Bluesky" },
  { value: "mastodon", label: "Mastodon" },
  { value: "tiktok", label: "TikTok" },
  { value: "youtube", label: "YouTube" },
] as const;
export type SocialChannelService = (typeof SOCIAL_NETWORK_CHOICES)[number]["value"];
export const createSocialChannelBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  name: z.string().trim().min(1, "Give the channel a name.").max(80),
  service: z.enum(["linkedin", "x", "facebook", "instagram", "threads", "bluesky", "mastodon", "tiktok", "youtube"]),
});

export const socialPostSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  channelId: z.string(),
  channelName: z.string(),
  service: z.string(),
  text: z.string(),
  status: socialStatusSchema,
  /** When it should be published, ISO 8601 UTC. */
  scheduledFor: z.string().nullable(),
  /** The article this post promotes, when it was started from one. */
  contentId: z.string().nullable().default(null),
  /** The earlier post this one is a reuse of. */
  reusedFrom: z.string().nullable().default(null),
  /** The note from a reviewer who asked for changes. */
  note: z.string().nullable(),
  /** Direct links to the pictures, or one video file (not both). A link must stay reachable until the post is published. */
  imageUrls: z.array(z.string()).default([]),
  videoUrl: z.string().nullable().default(null),
  youtubeTitle: z.string().nullable().default(null),
  /** A short topic tag such as "Fair hiring", or null. */
  topic: z.string().nullable().default(null),
  /** After sending through Sociamonials (null until then). */
  smPostId: z.number().int().nullable().default(null),
  smStatus: z.enum(["draft", "pending_approval", "scheduled", "queued", "delivered", "failed", "canceled", "unknown"]).nullable().default(null),
  smMode: z.enum(["scheduled", "draft"]).nullable().default(null),
  smError: z.string().nullable().default(null),
  smSentAt: z.string().nullable().default(null),
  createdBy: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** v92: the same fields ads have. Null/empty for posts written by hand or before. */
  product: z.enum(["sonic_hire", "raya"]).nullable().default(null),
  ideaId: z.string().nullable().default(null),
  suggestedBy: z.string().nullable().default(null),
  writtenBy: z.string().nullable().default(null),
  tags: z.object({ hookTactic: z.string().optional(), ctaStyle: z.string().optional() }).nullable().default(null),
  problems: z.array(adProblemSchema).default([]),
  sceneImageId: z.string().nullable().default(null),
  suggestedTime: z.string().nullable().default(null),
  isReference: z.boolean().default(false),
  referenceSource: z.enum(["auto", "manual"]).nullable().default(null),
  verdictTags: z.array(z.string()).default([]),
});
export type SocialPost = z.infer<typeof socialPostSchema>;

const isoInstant = z.string().datetime({ offset: true });
export const createSocialPostsBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  channelIds: z.array(z.string().min(1).max(200)).min(1).max(10),
  text: z.string().trim().min(1).max(70_000),
  scheduledFor: isoInstant.nullable().optional(),
  /** Set when the post is written for a published article, so it stops showing under "No posts yet". */
  contentId: z.string().min(1).max(100).nullable().optional(),
  imageUrls: socialImageUrlsSchema.optional(),
  videoUrl: mediaUrl.nullable().optional(),
  youtubeTitle: z.string().trim().min(1).max(YOUTUBE_TITLE_MAX).nullable().optional(),
  topic: z.string().max(200).nullable().optional(),
});
export type CreateSocialPostsBody = z.infer<typeof createSocialPostsBodySchema>;
export const updateSocialPostBodySchema = z.object({
  text: z.string().trim().min(1).max(70_000).optional(),
  scheduledFor: isoInstant.nullable().optional(),
  imageUrls: socialImageUrlsSchema.optional(),
  videoUrl: mediaUrl.nullable().optional(),
  youtubeTitle: z.string().trim().min(1).max(YOUTUBE_TITLE_MAX).nullable().optional(),
  topic: z.string().max(200).nullable().optional(),
});
export const socialNoteBodySchema = z.object({ note: z.string().trim().min(3).max(500) });

export const socialStatusResponseSchema = z.object({
  channels: z.array(socialChannelSchema),
});
export const socialPostsResponseSchema = z.object({ items: z.array(socialPostSchema), total: z.number().int() });
export const socialPostsQuerySchema = z.object({
  siteId: z.string().min(1).max(100),
  status: socialStatusSchema.optional(),
  /** A view by where the post stands. "attention" means failed or missed its time. */
  stage: z.enum(["draft", "waiting", "approved", "scheduled", "posted", "failed", "cancelled", "attention"]).optional(),
  topic: z.string().max(100).optional(),
  /** Words to look for in the post text or the channel name. */
  q: z.string().trim().max(100).optional(),
  channelId: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

// --- articles that have no social post yet ---

export const UNPROMOTED_DAYS = 45;
export const unpromotedItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: z.enum(["article", "case_study", "ebook"]),
  publishedAt: z.string(),
  /** The article's address; null for sites where the CMS does not know the page address. */
  link: z.string().nullable(),
});
export const unpromotedResponseSchema = z.object({ days: z.number().int(), items: z.array(unpromotedItemSchema) });

// --- bulk drafts from a CSV file ---

export const SOCIAL_CSV_MAX_ROWS = 100;
export const importSocialCsvBodySchema = z.object({ siteId: z.string().min(1).max(100), csv: z.string().min(1).max(500_000) });
export const importSocialCsvResponseSchema = z.object({
  created: z.number().int(),
  errors: z.array(z.object({ line: z.number().int(), message: z.string() })),
});

/** RFC 4180 reading: quoted fields may hold commas, line breaks and doubled quotes. A leading byte-order mark is ignored. */
export function parseCsv(input: string): string[][] {
  const text = input.replace(/^\uFEFF/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"' && field === "") quoted = true;
    else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((f) => f.trim() !== "")) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== "")) rows.push(row);
  return rows;
}

export interface SocialCsvRow {
  /** Line number in the file (the header is line 1 when there are no line breaks inside fields). */
  line: number;
  channel: string;
  text: string;
  scheduledFor: string | null;
}

/**
 * Reads a file with the columns `channel`, `text` and (optional) `publish_at`. `publish_at` is either a full time with
 * its offset ("2026-11-02T09:00:00+05:00" or "…Z") or a plain "2026-11-02 09:00" read in the site's own time zone.
 * Returns the usable rows and a plain-language list of what is wrong; the caller imports nothing when anything is wrong.
 */
export function parseSocialCsv(input: string, tz: string, nowMs: number): { rows: SocialCsvRow[]; errors: Array<{ line: number; message: string }> } {
  const table = parseCsv(input);
  const errors: Array<{ line: number; message: string }> = [];
  if (table.length === 0) return { rows: [], errors: [{ line: 1, message: "The file is empty." }] };
  const header = table[0]!.map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  const col = (name: string) => header.indexOf(name);
  const cChannel = col("channel");
  const cText = col("text");
  const cWhen = col("publish_at");
  if (cChannel < 0 || cText < 0) return { rows: [], errors: [{ line: 1, message: "The first line must name the columns: channel, text and (optionally) publish_at." }] };
  if (table.length - 1 > SOCIAL_CSV_MAX_ROWS) return { rows: [], errors: [{ line: 1, message: `That is ${table.length - 1} posts; the limit is ${SOCIAL_CSV_MAX_ROWS} at a time.` }] };
  const rows: SocialCsvRow[] = [];
  table.slice(1).forEach((cells, i) => {
    const line = i + 2;
    const channel = (cells[cChannel] ?? "").trim();
    const text = (cells[cText] ?? "").trim();
    const whenRaw = cWhen >= 0 ? (cells[cWhen] ?? "").trim() : "";
    if (!channel) return void errors.push({ line, message: "The channel is empty." });
    if (!text) return void errors.push({ line, message: "The text is empty." });
    let scheduledFor: string | null = null;
    if (whenRaw) {
      let ms = NaN;
      const plain = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})$/.exec(whenRaw);
      if (plain) ms = zonedToInstant({ year: +plain[1]!, month: +plain[2]!, day: +plain[3]!, hour: +plain[4]!, minute: +plain[5]! }, tz);
      else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(whenRaw)) ms = Date.parse(whenRaw);
      if (Number.isNaN(ms)) return void errors.push({ line, message: `“${whenRaw}” is not a time I can read. Use 2026-11-02 09:00 or 2026-11-02T09:00:00+05:00.` });
      if (ms <= nowMs) return void errors.push({ line, message: "That time has already passed." });
      scheduledFor = new Date(ms).toISOString();
    }
    rows.push({ line, channel, text, scheduledFor });
  });
  return { rows, errors };
}
